import { useLabsExperiment, useLabView, useLabEvent } from "./labExperiment";
import { useEffect, useRef, useState, type FormEvent } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Button, Drawer } from "@/design-system/primitives";
import {
  AddressFields,
  EditField,
} from "@/features/account/components/AccountFields";
import { PaymentMethodPicker } from "@/features/marketplace/components/PaymentMethodPicker";
import { OrdersScreen } from "@/features/orders/OrdersScreen";
import { ApiError } from "@/lib/api/client";
import { usePatient } from "@/lib/api/hooks";
import {
  useCreateLabCheckout,
  useLabCatalog,
  useLabCheckoutStatus,
} from "@/lib/api/labHooks";
import { useAuth } from "@/lib/auth/AuthProvider";
import { getStripe } from "@/lib/stripe";
import type {
  LabCheckoutInput,
  LabCheckoutResponse,
  LabPanel,
} from "@/types/labs";
import type { Patient } from "@/types/api";
import { LabError, LabSkeleton } from "./LabOrderContent";
import { labDisplayName, labMoney } from "./labPresentation";
import {
  clearLabCheckoutAttempt,
  createLabCheckoutAttempt,
  readLabCheckoutAttempt,
  type LabCheckoutAttempt,
} from "./labCheckoutStorage";

export function LabCheckoutScreen() {
  const labs = useLabsExperiment();
  const emit = useLabEvent();
  const { panelCode } = useParams();
  const navigate = useNavigate();
  const catalog = useLabCatalog(labs.enabled);
  const patient = usePatient();
  const { session } = useAuth();
  const productId = panelCode && ["Y499", "Y286", "Y296"].includes(panelCode)
    ? `tasso-${panelCode.toLowerCase()}` : null;
  const savedAttempt =
    session?.user.id && productId
      ? readLabCheckoutAttempt(session.user.id, productId)
      : null;
  useLabView(
    "blood_test_checkout_viewed",
    "checkout_drawer",
    labs.enabled || Boolean(savedAttempt),
  );
  const panel = catalog.data?.enabled
    ? catalog.data.items.find(
        (item) => item.panel_code === panelCode && item.purchasable,
      )
    : undefined;
  if (!savedAttempt && labs.loading) return <LabSkeleton />;
  if (!savedAttempt && !labs.enabled) return <Navigate to="/app" replace />;
  if (!savedAttempt) return <Navigate to="/app/shop?tab=labs" replace />;
  return (
    <>
      <OrdersScreen />
      <Drawer
        open
        title="Blood test checkout"
        onClose={() => {
          emit("blood_test_checkout_closed", { source: "checkout_drawer" });
          navigate("/app/orders");
        }}
      >
        <div className="ph-no-capture p-15">
          {savedAttempt && session?.user.id && productId ? (
            <LabCheckoutForm
              key={`${session.user.id}:${productId}`}
              panel={panel}
              patient={patient.data}
              ownerId={session.user.id}
              productId={productId}
              recoveryOnly
            />
          ) : catalog.isLoading || patient.isLoading ? (
            <LabSkeleton label="Fetching your checkout" />
          ) : catalog.isError || patient.isError ? (
            <LabError
              message="We could not retrieve your checkout details."
              onRetry={() => {
                void catalog.refetch();
                void patient.refetch();
              }}
            />
          ) : !panel || !patient.data || !session?.user.id ? (
            <LabError message="This blood test is not available to purchase." />
          ) : (
            <LabCheckoutForm
              key={`${session.user.id}:${panel.product_id}`}
              panel={panel}
              patient={patient.data}
              ownerId={session.user.id}
              productId={panel.product_id}
            />
          )}
        </div>
      </Drawer>
    </>
  );
}

function LabCheckoutForm({
  panel,
  patient,
  ownerId,
  productId,
  recoveryOnly = false,
}: {
  panel?: LabPanel;
  patient?: Patient;
  ownerId: string;
  productId: string;
  recoveryOnly?: boolean;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const emit = useLabEvent();
  const create = useCreateLabCheckout();
  const status = useLabCheckoutStatus();
  const [paymentMethodId, setPaymentMethodId] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const confirmingRef = useRef(false);
  const [details, setDetails] = useState({
    first_name: patient?.first_name || "",
    last_name: patient?.last_name || "",
    dob: patient?.date_of_birth || "",
    assigned_sex: (["male", "female", "other"].includes(
      patient?.sex?.toLowerCase() ?? "",
    )
      ? patient?.sex.toLowerCase()
      : "") as LabCheckoutInput["assigned_sex"] | "",
    phone: patient?.phone || "",
    line1: patient?.address?.line1 || "",
    line2: patient?.address?.line2 || "",
    city: patient?.address?.city || "",
    state: patient?.address?.state || "",
    postal_code: patient?.address?.postal_code || "",
  });
  const [consent, setConsent] = useState(false);
  const [attempt, setAttempt] = useState<LabCheckoutAttempt | null>(() =>
    readLabCheckoutAttempt(ownerId, productId),
  );
  const [checkout, setCheckout] = useState<LabCheckoutResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [paymentMessage, setPaymentMessage] = useState<string | null>(null);
  const recovering = useRef(false);
  const starting = useRef(false);
  const [terminal, setTerminal] = useState(false);
  const mountedOwner = useRef(ownerId);
  useEffect(() => {
    mountedOwner.current = ownerId;
    return () => {
      mountedOwner.current = "";
    };
  }, [ownerId]);
  const current = () => mountedOwner.current === ownerId;

  async function verify(saved: LabCheckoutAttempt) {
    setError(null);
    try {
      const result = await status.mutateAsync({
        checkout_id: saved.request_id,
        checkout_token: saved.checkout_token,
      });
      if (!current()) return;
      if (result.status === "paid" && result.lab_order_id) {
        emit("blood_test_confirmation_viewed", {
          source: "checkout_drawer",
          product_id: productId,
        });
        clearLabCheckoutAttempt(ownerId, productId);
        await queryClient.invalidateQueries({ queryKey: ["bootstrap"] });
        if (!current()) return;
        navigate(
          `/app/orders?lab_order=${encodeURIComponent(result.lab_order_id)}&purchased=1`,
          {
            replace: true,
          },
        );
      } else if (
        result.status === "cancelled" ||
        result.status === "refunded"
      ) {
        setTerminal(true);
        setCheckout(null);
        setPaymentMessage(
          result.status === "refunded"
            ? "This test payment was refunded. You can review your blood tests for the latest update."
            : "This payment was cancelled. No new charge has been started.",
        );
      } else if (
        result.status === "pending_payment" &&
        result.client_secret &&
        result.publishable_key &&
        result.payment_intent_id &&
        typeof result.amount_cents === "number" &&
        result.currency
      ) {
        setCheckout({
          checkout_id: saved.request_id,
          client_secret: result.client_secret,
          publishable_key: result.publishable_key,
          payment_intent_id: result.payment_intent_id,
          amount_cents: result.amount_cents,
          currency: result.currency,
          status: result.status,
          payment_status: result.payment_status,
        });
        setPaymentMessage(null);
      } else {
        setCheckout(null);
        setPaymentMessage(
          "Your payment is being confirmed. Check its status again to continue; you do not need to purchase again.",
        );
      }
    } catch {
      if (current())
        setError(
          "We could not confirm this checkout yet. Check its status again before starting another purchase.",
        );
    }
  }

  useEffect(() => {
    if (attempt && !recovering.current) {
      recovering.current = true;
      void verify(attempt);
    }
    // One attempt is restored for the exact account/product. Never create payment on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function start(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (
      !panel ||
      !patient ||
      recoveryOnly ||
      !consent ||
      !paymentMethodId ||
      !details.assigned_sex ||
      starting.current ||
      attempt
    )
      return;
    emit("blood_test_checkout_started", {
      source: "checkout_drawer",
      product_id: productId,
      purchase_type: "one_time",
    });
    starting.current = true;
    setError(null);
    let saved: LabCheckoutAttempt;
    try {
      saved = createLabCheckoutAttempt(ownerId, productId);
    } catch {
      setError(
        "Enable browser storage before continuing so your payment can be recovered if the page reloads.",
      );
      starting.current = false;
      return;
    }
    setAttempt(saved);
    try {
      const response = await create.mutateAsync({
        payment_method_id: paymentMethodId,
        product_id: productId,
        request_id: saved.request_id,
        checkout_token: saved.checkout_token,
        expected_amount_cents: panel.one_time_price_cents,
        email: patient.email,
        first_name: details.first_name.trim(),
        last_name: details.last_name.trim(),
        dob: details.dob,
        assigned_sex: details.assigned_sex,
        phone: details.phone.trim(),
        shipping: {
          line1: details.line1.trim(),
          line2: details.line2.trim() || undefined,
          city: details.city.trim(),
          state: details.state.trim().toUpperCase(),
          postal_code: details.postal_code.trim(),
          country: "US",
        },
        intake: { version: "tasso-y499-staging-v1", consent: true },
      });
      if (!current()) return;
      if (response.payment_status === "requires_action")
        await confirmExisting(response, saved, paymentMethodId);
      else if (
        ["succeeded", "processing"].includes(response.payment_status ?? "") ||
        response.status === "paid"
      )
        await verify(saved);
      else {
        setCheckout(response);
        setError("Choose a payment method to complete your saved purchase.");
      }
    } catch (cause) {
      if (!current()) return;
      emit("blood_test_checkout_failed", {
        source: "checkout_drawer",
        product_id: productId,
        reason:
          cause instanceof ApiError ? `http_${cause.status}` : "transport",
      });
      const notCreated =
        cause instanceof ApiError &&
        (cause.status === 422 ||
          (cause.details as { checkout_not_created?: boolean } | null)
            ?.checkout_not_created === true);
      if (notCreated) {
        clearLabCheckoutAttempt(ownerId, productId);
        setAttempt(null);
        setError(
          cause instanceof ApiError && cause.status !== 422
            ? cause.message
            : "Check your contact and delivery details before continuing.",
        );
        void queryClient.invalidateQueries({ queryKey: ["labs", "catalog"] });
      } else
        setError(
          "We could not confirm that checkout started. Check its status below to recover the same payment.",
        );
    } finally {
      starting.current = false;
    }
  }

  async function confirmExisting(
    response: LabCheckoutResponse,
    saved: LabCheckoutAttempt,
    pmId = paymentMethodId,
  ) {
    if (confirmingRef.current) return;
    confirmingRef.current = true;
    setConfirming(true);
    setError(null);
    setCheckout(null);
    try {
      if (
        !["succeeded", "processing"].includes(response.payment_status ?? "") &&
        response.status !== "paid"
      ) {
        const stripe = await getStripe(response.publishable_key);
        if (!current()) return;
        if (!stripe) throw new Error("Payment service unavailable");
        // Matches the existing supplement checkout: confirm the original intent,
        // including any bank challenge. Never create a second intent on decline.
        const result = await stripe.confirmCardPayment(
          response.client_secret,
          response.payment_status === "requires_action"
            ? undefined
            : pmId
              ? { payment_method: pmId }
              : undefined,
        );
        if (!current()) return;
        if (result.error) {
          // A bank challenge can change the intent into a card-decline state.
          // Refresh that same intent before offering an explicit card retry.
          await verify(saved);
          if (!current()) return;
          setError(
            result.error.message ??
              "Your payment could not be completed. Choose a card and try again.",
          );
          return;
        }
      }
      if (!current()) return;
      setPaymentMessage(
        "Your payment was submitted. We are confirming your test order.",
      );
      await verify(saved);
    } catch {
      if (current())
        setError(
          "We could not confirm this checkout yet. Check its status again before starting another purchase.",
        );
    } finally {
      confirmingRef.current = false;
      if (current()) setConfirming(false);
    }
  }

  const busy = create.isPending || status.isPending || confirming;
  const field = (
    name: "first_name" | "last_name" | "dob" | "phone",
    label: string,
    type = "text",
  ) => (
    <EditField
      label={label}
      name={name}
      value={details[name]}
      onChange={(value) => setDetails((old) => ({ ...old, [name]: value }))}
      type={type}
      required
      {...(name === "dob"
        ? { min: "1900-01-01", max: new Date().toISOString().slice(0, 10) }
        : {})}
    />
  );
  return (
    <div className="flex flex-col gap-15">
      <div className="border-b border-line-subtle pb-15">
        <p className="type-label-xs text-ink-muted">
          At-home blood testing · One-time purchase
        </p>
        <h1 className="font-display text-24 mt-8">
          {panel ? labDisplayName(panel.name) : "Your blood test checkout"}
        </h1>
        <p className="type-body-sm text-ink-secondary mt-8">
          {panel?.kit_description ?? "Recover your saved purchase."}
        </p>
      </div>
      {attempt ? (
        <>
          {busy && <LabSkeleton label="Confirming your checkout" />}
          {error && (
            <p role="alert" className="type-body-sm text-danger">
              {error}
            </p>
          )}
          {paymentMessage && (
            <p role="status" className="type-body-sm text-ink-secondary">
              {paymentMessage}
            </p>
          )}
          {checkout && (
            <>
              <PaymentMethodPicker
                selectedPmId={paymentMethodId}
                onSelect={setPaymentMethodId}
                enabled={!busy}
              />
              <Button
                disabled={
                  busy ||
                  (!paymentMethodId &&
                    checkout.payment_status !== "requires_action")
                }
                loading={confirming}
                onClick={() => void confirmExisting(checkout, attempt)}
              >
                Pay {labMoney(checkout.amount_cents, checkout.currency)} once
              </Button>
            </>
          )}
          {!terminal && (
            <Button
              variant="secondary"
              disabled={busy}
              onClick={() => void verify(attempt)}
            >
              Check payment status
            </Button>
          )}
          <Button asChild variant="ghost">
            <Link to="/app/orders">View your orders</Link>
          </Button>
        </>
      ) : !panel || !patient || recoveryOnly ? (
        <LabError message="This checkout is not available to start again. Return to your orders for help." />
      ) : (
        <>
          <form
            id="lab-checkout"
            onSubmit={start}
            className="flex flex-col gap-15"
          >
            <h2 className="type-label-sm">Your details</h2>
            <div className="grid grid-cols-2 gap-15">
              {field("first_name", "First name")}
              {field("last_name", "Last name")}
            </div>
            {field("dob", "Date of birth", "date")}
            <EditField label="Sex assigned at birth">
              <select
                required
                value={details.assigned_sex}
                onChange={(event) =>
                  setDetails((value) => ({
                    ...value,
                    assigned_sex: event.target
                      .value as LabCheckoutInput["assigned_sex"],
                  }))
                }
                className="h-40 px-15 bg-white border border-[#ECECEC] text-black text-13 focus-visible:shadow-focus"
              >
                <option value="" disabled>
                  Select
                </option>
                <option value="female">Female</option>
                <option value="male">Male</option>
                <option value="other">Other</option>
              </select>
            </EditField>
            {field("phone", "Phone", "tel")}
            <p className="type-body-sm text-ink-muted break-all">
              Account email: {patient.email}
            </p>
            <h2 className="type-label-sm border-t border-line-subtle pt-15">
              Shipping address
            </h2>
            <AddressFields
              usOnly
              value={{
                line1: details.line1,
                line2: details.line2,
                city: details.city,
                state: details.state,
                postal_code: details.postal_code,
                country: "US",
              }}
              onChange={(address) =>
                setDetails((value) => ({
                  ...value,
                  ...address,
                  line2: address.line2 ?? "",
                }))
              }
            />
            <label className="flex items-start gap-10 type-body-sm leading-145 border-y border-line-subtle py-15">
              <input
                type="checkbox"
                checked={consent}
                required
                onChange={(event) => setConsent(event.target.checked)}
                className="mt-3 shrink-0 accent-accent"
              />
              I confirm these details are accurate and consent to at-home sample
              collection, laboratory testing, and sharing the results with my
              System Labs care team.
            </label>
          </form>
          <PaymentMethodPicker
            selectedPmId={paymentMethodId}
            onSelect={setPaymentMethodId}
          />
          {panel.is_test_price && (
            <p className="type-body-sm text-ink-muted">
              Staging test price. No subscription.
            </p>
          )}
          {error && (
            <p role="alert" className="type-body-sm text-danger">
              {error}
            </p>
          )}
          <div className="flex items-center justify-between gap-15 border-t border-line-subtle pt-15">
            <div>
              <p className="type-label-xs text-ink-muted">TOTAL TODAY</p>
              <p className="font-display text-24 mt-5">
                {labMoney(panel.one_time_price_cents, panel.currency)}
              </p>
            </div>
            <Button
              type="submit"
              form="lab-checkout"
              disabled={!consent || !paymentMethodId || busy}
              loading={busy}
            >
              Confirm purchase
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
